import { motion } from "framer-motion";
import {
  Apple,
  ArrowRight,
  AudioLines,
  Bell,
  BookOpenText,
  Bookmark,
  CircleCheckBig,
  Globe,
  HeartHandshake,
  NotebookPen,
  Search,
  Sparkles,
  Target,
} from "lucide-react";
import { apps } from "@/data/apps";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
};

const features = [
  {
    icon: <AudioLines className="w-5 h-5" />,
    title: "Sermon recording",
    description:
      "Capture the message as it happens so you never miss an insight or key moment again.",
  },
  {
    icon: <NotebookPen className="w-5 h-5" />,
    title: "Automatic sermon transcripts",
    description:
      "Selah Daily converts your recording into a clean, searchable sermon transcript you can revisit anytime.",
  },
  {
    icon: <BookOpenText className="w-5 h-5" />,
    title: "AI-generated devotional plans",
    description:
      "Turn one sermon into a personalized five-day devotional journey with key themes, Scripture reflections, and practical application.",
  },
  {
    icon: <HeartHandshake className="w-5 h-5" />,
    title: "Guided prayer wall",
    description:
      "Pray intentionally with topic-based guided prayers for gratitude, strength, direction, faith, and everyday life.",
  },
  {
    icon: <Search className="w-5 h-5" />,
    title: "Bible reading tools",
    description:
      "Read with search, bookmark verses, and capture personal reflection notes in one place.",
  },
  {
    icon: <Globe className="w-5 h-5" />,
    title: "Multi-language devotionals",
    description:
      "Translate devotionals into multiple languages so churches and believers can grow together globally.",
  },
  {
    icon: <Bell className="w-5 h-5" />,
    title: "Daily reminders and streaks",
    description:
      "Build a consistent spiritual habit with reminders, streak tracking, and progress statistics.",
  },
];

const steps = [
  {
    number: "1",
    title: "Record the sermon",
    description: "Capture the message during church.",
  },
  {
    number: "2",
    title: "Generate devotionals",
    description: "Selah Daily turns the sermon into a transcript, summary, and five-day devotional plan.",
  },
  {
    number: "3",
    title: "Reflect daily",
    description: "Spend a few minutes each day reflecting on the message.",
  },
  {
    number: "4",
    title: "Grow spiritually",
    description: "Apply biblical truth consistently throughout your week.",
  },
];

const audiences = [
  "Church members who want to remember sermons",
  "Pastors who want their message to last beyond Sunday",
  "Small groups who want structured weekly devotionals",
  "Christians seeking a deeper daily walk with God",
];

const distinctives = [
  "Rooted in the local church",
  "Scripture-based devotionals",
  "Clean, distraction-free reading",
  "Built for consistent spiritual habits",
];

const bibleTools = [
  "Bible reading with search",
  "Verse bookmarks",
  "Personal reflection notes",
];

const selahDailyApp = apps.find((app) => app.id === "selah-daily");

const SelahDailyPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="selah-daily-page">
      <section className="min-h-[calc(100vh-5rem)] flex items-center py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center"
          >
            <div className="max-w-4xl">
              <p className="caption mb-4">Selah Daily</p>
              <h1 className="heading-1 mb-6">
                Turn every sermon into {" "}
                <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                  daily spiritual growth.
                </span>
              </h1>
              <p className="body-lg max-w-3xl mb-6">
                In a fast and noisy world, it&apos;s easy to hear a message on Sunday and
                forget it by Monday.
              </p>
              <p className="body-text max-w-2xl mb-8">
                Selah Daily helps you pause, reflect, and grow in your faith every day
                of the week. Record sermons, generate devotionals instantly, and carry
                God&apos;s message with you long after the service ends.
              </p>
              <div className="flex flex-wrap gap-3 text-sm font-medium text-zinc-600 mb-10">
                <span className="rounded-full bg-zinc-100 px-4 py-2">Pause</span>
                <span className="rounded-full bg-zinc-100 px-4 py-2">Reflect</span>
                <span className="rounded-full bg-zinc-100 px-4 py-2">Grow</span>
              </div>
              {selahDailyApp?.appStoreUrl && (
                <a
                  href={selahDailyApp.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="selah-daily-hero-download"
                >
                  <Apple className="w-4 h-4" />
                  Download Selah Daily
                </a>
              )}
            </div>

            <motion.div
              {...fadeInUp}
              className="relative rounded-[2.5rem] bg-[#F5F5F7] border border-zinc-100 p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.14),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(147,51,234,0.12),transparent_30%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm">
                  <Sparkles className="w-4 h-4 text-fuchsia-500" />
                  Sunday message, all week long
                </div>
                <div className="mt-8 space-y-4">
                  <div className="rounded-[1.75rem] bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">This week&apos;s message</p>
                    <p className="text-base font-medium text-zinc-900">Walking by faith in uncertain seasons</p>
                    <p className="text-sm text-zinc-500 mt-2">Sermon recorded and converted into a devotional plan</p>
                  </div>
                  <div className="rounded-[1.75rem] bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Daily rhythm</p>
                    <div className="space-y-2 text-sm text-zinc-600">
                      <p>Scripture reflection</p>
                      <p>Prayer prompt</p>
                      <p>Simple application for today</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center mb-16">
            <p className="caption mb-4">Why Selah Daily?</p>
            <h2 className="heading-2 mb-6">From Sunday inspiration to daily transformation.</h2>
            <div className="space-y-4 body-text">
              <p>
                Most people hear a powerful sermon but struggle to remember it during
                the week. Selah Daily bridges that gap.
              </p>
              <p>
                It helps you capture, reflect on, and apply sermons throughout the
                week, transforming one message into meaningful daily devotion.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {distinctives.map((item) => (
              <motion.div key={item} variants={fadeInUp} className="feature-card text-center">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-5 text-zinc-600">
                  <CircleCheckBig className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-medium text-zinc-900">{item}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Key Features</p>
            <h2 className="heading-2 mb-6">Built for believers who want to stay rooted.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {features.map((feature) => (
              <motion.div key={feature.title} variants={fadeInUp} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Bible Reading Tools</p>
              <h2 className="heading-2 mb-6">A clean, distraction-free faith experience.</h2>
              <p className="body-text mb-8">
                Selah Daily is intentionally designed to help you focus. No clutter. No
                distractions. Just Scripture, reflection, and prayer.
              </p>
              <div className="space-y-4">
                {bibleTools.map((tool) => (
                  <div key={tool} className="flex items-start gap-3">
                    <Bookmark className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{tool}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Daily Rhythm</p>
              <h3 className="heading-3 mb-4">Small habits, lasting growth.</h3>
              <p className="body-text mb-6">
                Daily reminders, streak tracking, and progress stats help believers stay
                consistent. Small daily rhythms lead to lasting spiritual growth.
              </p>
              <div className="rounded-[1.5rem] bg-[#F5F5F7] p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-400 mb-3">
                  The idea behind Selah
                </p>
                <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-900">
                  Pause from the noise. Reflect on God&apos;s Word. Grow in your faith every day.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">How Selah Daily Works</p>
            <h2 className="heading-2 mb-6">Simple rhythms for steady growth.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
          >
            {steps.map((step) => (
              <motion.div key={step.number} variants={fadeInUp} className="feature-card">
                <p className="text-sm font-semibold tracking-[0.2em] text-zinc-400 mb-5">{step.number}</p>
                <h3 className="heading-3 mb-4">{step.title}</h3>
                <p className="body-text">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Built for Churches and Believers</p>
              <h2 className="heading-2 mb-6">Let the message live beyond the pulpit.</h2>
              <div className="space-y-4">
                {audiences.map((audience) => (
                  <div key={audience} className="flex items-start gap-3">
                    <Target className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{audience}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">What Makes It Different</p>
              <h3 className="heading-3 mb-4">Faith-focused by design.</h3>
              <p className="body-text mb-6">
                Selah Daily was built around the biblical idea of pausing to reflect.
                It helps the church message shape everyday life through Scripture,
                prayer, and practical daily application.
              </p>
              <div className="rounded-[1.5rem] bg-[#F5F5F7] p-6">
                <p className="text-sm text-zinc-600 leading-relaxed">
                  The word &ldquo;Selah&rdquo; appears in the Psalms and is often understood as
                  a moment to pause and reflect.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-zinc-900">
        <div className="container-main text-center">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <p className="caption text-zinc-500 mb-4">Download Selah Daily Today</p>
            <h2 className="heading-2 text-white mb-6">
              Start your daily rhythm with God.
            </h2>
            <p className="text-lg text-zinc-400 leading-relaxed mb-10">
              Available on iPhone and iPad. Start turning sermons into daily spiritual
              growth with a simple, focused devotional experience.
            </p>
            {selahDailyApp?.appStoreUrl && (
              <a
                href={selahDailyApp.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="selah-daily-final-cta"
              >
                Download on the App Store
                <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SelahDailyPage;
